import React, { useEffect, useState } from "react";
import FarsightApi from "../../../ApiWrapper";
import { Box, Grow, lighten } from "@mui/material";
import AdaptablePageSizeGrid from "../AdaptablePageSizeGrid";
import { loss_color, win_color } from "../../../static_vars";
import StyledDatagrid, { ColumnTypes, field_name_to_column } from "../styled_datagrid";
import {GridColumnGroupingModel, GridEnrichedColDef} from "@mui/x-data-grid";
import { GameMetricsApi, GameMetrics } from "../../../farsight-api";
import { GridRowClassNameParams } from "@mui/x-data-grid/models/params";
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";

export enum rowStyle {
    none,
    outcome,
    prediction,
    win
}

const styleToComponent = {
    [rowStyle.none]: (params: GridRowClassNameParams) => "None",
    [rowStyle.outcome]: (params: GridRowClassNameParams) => {
        return `rowStyle--${params.row.winner === params.row.team1 ? "Win" : "Loss"}`;
    },
    [rowStyle.prediction]: (params: GridRowClassNameParams) => {
        return `rowStyle--${params.row.winner === params.row.drafting_sim_predicted_winner ? "Win" : "Loss"}`;
    },
    [rowStyle.win]: (params: GridRowClassNameParams) => {
        return `rowStyle--${params.row.win? "Win" : "Loss"}`;
    },
};

function getColumnsFromProperties(display_all: boolean, metrics?: GameMetrics[], disp_columns?: string[], result?: any[]) {
    let new_columns: GridEnrichedColDef[] =
        display_all && result !== undefined && result.length > 0
            ? Object.entries(result[0])
                  .map(([key, value]) => field_name_to_column[key])
                  .filter((col) => col !== undefined && display_all)
            : (disp_columns ? disp_columns : []).map((col) => {
                  const existing_definition = field_name_to_column[col];
                  return existing_definition ? existing_definition : ColumnTypes.string(col, col);
              });
    if (!display_all && metrics !== undefined && metrics.length > 0) {
        const metrics_columns = metrics
            ? (metrics.map((metric: GameMetrics) => field_name_to_column[metric.code] !== undefined && metric.type === 'string' ? field_name_to_column[metric.code] :
                  ColumnTypes[metric.type ? metric.type : 'string'](metric.code, metric.short_name, {
                      description: metric.desc,
                  }),
              ) as GridEnrichedColDef[])
            : ([] as GridEnrichedColDef[]);
        new_columns = [...new_columns, ...metrics_columns];
        // console.log(new_columns)
    }
    return new_columns;
}

export default function TableDisplayGrid(props: {
    request: () => any;
    display_all: boolean;
    disp_columns?: string[];
    custom_columns?: GridEnrichedColDef[];
    metrics?: GameMetrics[];
    rowStyle: rowStyle;
    sortBy: string;
    paramObject: any;
    hideFooter?: boolean;
    grouping_model?: GridColumnGroupingModel;
}) {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // console.log(props.disp_columns);
    const [columns, setColumns] = useState<GridEnrichedColDef[]>(getColumnsFromProperties(props.display_all, props.metrics, props.disp_columns));

    const initialState = {
        sorting: {
            sortModel: [{ field: props.sortBy, sort: "desc" }],
        },
    } as GridInitialStateCommunity;

    useEffect(() => {
        setLoading(true);
        setRows([]);
        props.request().then((result: any[]) => {
            if (result) {
                const new_columns = getColumnsFromProperties(props.display_all, props.metrics, props.disp_columns, result);
                setColumns(new_columns);
                setLoading(false);
                setRows(result);
            } else {
                setLoading(false);
                setRows([]);
            }
        });
    }, [props.paramObject.watermark, props.metrics]);

    return (
        <Grow in={true} style={{ transformOrigin: "0 -50 0" }} {...{ timeout: 2000 }}>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    "& .border-right": {
                        borderRight: "solid grey",
                        borderWidth: "2px",
                    },
                }}
            >
                <StyledDatagrid
                    autoPageSize
                    disableSelectionOnClick
                    rowHeight={50}
                    getRowId={(row) => rows.indexOf(row)}
                    initialState={initialState}
                    experimentalFeatures={{ columnGrouping: true }}
                    columnGroupingModel={props.grouping_model}
                    sx={{
                        width: "100%",
                        "& .rowStyle--Win": {
                            backgroundColor: win_color,
                            opacity: 0.8,
                            "&:hover": {
                                backgroundColor: lighten(win_color, 0.1),
                            },
                            "&.Mui-selected": {
                                backgroundColor: lighten(win_color, 0.1),
                                "&:hover": {
                                    backgroundColor: lighten(win_color, 0.1),
                                },
                            },
                        },
                        "& .rowStyle--Loss": {
                            backgroundColor: loss_color,
                            opacity: 0.8,
                            "&:hover": {
                                backgroundColor: lighten(loss_color, 0.1),
                            },
                            "&.Mui-selected": {
                                backgroundColor: lighten(loss_color, 0.1),
                                "&:hover": {
                                    backgroundColor: lighten(loss_color, 0.1),
                                },
                            },
                        },
                    }}
                    columns={[...columns, ...(props.custom_columns ? props.custom_columns : [])]}
                    rows={rows}
                    hideFooter={props.hideFooter}
                    loading={loading}
                    rowBuffer={3}
                    pagination
                    getRowClassName={styleToComponent[props.rowStyle]}
                />
            </Box>
        </Grow>
    );
}
